import React, { useMemo, useState } from 'react';
import { Card, Button, Typography, Spin } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getApartmentUnitsByBlock, getBlockById, getBlocks } from '@/api/properties';
import { useAuth } from '@/contexts/AuthContext';
import FloorsTab from '@/components/tab/FloorsTab';
import type { ApartmentUnit } from '@/types/resources/property';

const { Text } = Typography;

function unitHasResident(unit: ApartmentUnit): boolean {
  return Boolean(unit.resident?.id) || unit.status === 'occupied';
}

interface ResidentAssignmentCardProps {
  blockIds: string[];
  onNext: () => void;
  onBack: () => void;
}

export const ResidentAssignmentCard: React.FC<ResidentAssignmentCardProps> = ({
  blockIds,
  onNext,
}) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0);

  // Fetch all blocks from API
  const { data: blocksData, isLoading: blocksLoading, error: blocksError } = useQuery({
    queryKey: ['blocks', firestoreUser?.workspace?.id],
    queryFn: () => getBlocks({ firestoreUser: firestoreUser! }),
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Get block IDs from API response or fallback to props
  const effectiveBlockIds = blocksData?.blocks?.map(block => block.id) || blockIds;

  // Check if we have valid block IDs
  const hasValidBlockIds = effectiveBlockIds.length > 0;

  const { data: allApartmentUnits, isLoading: unitsLoading } = useQuery({
    queryKey: [
      'onboardingAllUnits',
      firestoreUser?.workspace?.id,
      effectiveBlockIds.join(','),
    ],
    queryFn: async () => {
      const lists = await Promise.all(
        effectiveBlockIds.map((id) => getApartmentUnitsByBlock(id, firestoreUser!))
      );
      return lists.flatMap((l) => l.apartmentUnits);
    },
    enabled: !!firestoreUser && hasValidBlockIds,
  });

  const { totalUnits, occupiedUnits, allUnitsHaveResidents } = useMemo(() => {
    const units = allApartmentUnits ?? [];
    const total = units.length;
    const occupied = units.filter(unitHasResident).length;
    return {
      totalUnits: total,
      occupiedUnits: occupied,
      allUnitsHaveResidents: total > 0 && occupied === total,
    };
  }, [allApartmentUnits]);

  // Fetch the current block data
  const { data: currentBlock, isLoading, error } = useQuery({
    queryKey: ['block', effectiveBlockIds[currentBlockIndex]],
    queryFn: () => getBlockById(effectiveBlockIds[currentBlockIndex], firestoreUser!),
    enabled: !!effectiveBlockIds[currentBlockIndex] && !!firestoreUser && hasValidBlockIds,
    staleTime: 5 * 60 * 1000,
  });

  const handleResidentAssigned = () => {
    queryClient.invalidateQueries({ queryKey: ['onboardingAllUnits'] });
  };

  // Navigation handlers
  const handleNextBlock = () => {
    if (currentBlockIndex < effectiveBlockIds.length - 1) {
      setCurrentBlockIndex(currentBlockIndex + 1);
    }
  };

  const handlePreviousBlock = () => {
    if (currentBlockIndex > 0) {
      setCurrentBlockIndex(currentBlockIndex - 1);
    }
  };

  const handleNext = () => {
    if (!allUnitsHaveResidents) return;
    onNext();
  };

  if (blocksLoading || isLoading || unitsLoading) {
    return (
      <Card>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '40px' 
        }}>
          <Spin size="large" />
          <span style={{ marginLeft: 16 }}>Loading blocks...</span>
        </div>
      </Card>
    );
  }

  if (blocksError || !hasValidBlockIds) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="secondary">No blocks available for resident assignment.</Text>
          <div style={{ marginTop: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Please complete the block setup process first.
            </Text>
          </div>
        </div>
      </Card>
    );
  }

  if (error || !currentBlock) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="danger">Failed to load block details. Please try again.</Text>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      {/* Block Progress Indicators with Navigation */}
      {effectiveBlockIds.length > 1 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '8px',
          marginBottom: '20px'
        }}>
          <Button 
            icon={<LeftOutlined />} 
            onClick={handlePreviousBlock}
            disabled={currentBlockIndex === 0}
            size="small"
            type="text"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
          
          {effectiveBlockIds.map((_, index) => (
            <Button
              key={index}
              type={index === currentBlockIndex ? 'primary' : 'default'}
              size="small"
              onClick={() => setCurrentBlockIndex(index)}
              style={{
                minWidth: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              {index + 1}
            </Button>
          ))}
          
          <Button 
            icon={<RightOutlined />} 
            onClick={handleNextBlock}
            disabled={currentBlockIndex === effectiveBlockIds.length - 1}
            size="small"
            type="text"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        </div>
      )}

      {/* Instruction Text */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '16px',
        padding: '0 16px'
      }}>
        <Text type="secondary" style={{ fontSize: '14px' }}>
          {t('onboarding:cards.residentAssignment.instruction')}
        </Text>
      </div>

      {/* Floors Tab */}
      <FloorsTab
        blockId={effectiveBlockIds[currentBlockIndex]}
        blockName={currentBlock.name}
        floors={currentBlock.floors || []}
        onResidentAssigned={handleResidentAssigned}
      />

      {/* Action Buttons */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: '1px solid #f0f0f0'
      }}>
        <Button 
          type="primary" 
          size="large" 
          onClick={handleNext}
          disabled={!allUnitsHaveResidents}
          style={{ 
            minWidth: '150px',
            backgroundColor: '#52c41a',
            borderColor: '#52c41a'
          }}
        >
          {t('onboarding:cards.residentAssignment.continueToInvites', 'Continue to invitations')}
        </Button>
        
        {!allUnitsHaveResidents && totalUnits > 0 && (
          <div style={{ marginTop: '12px' }}>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {t('onboarding:cards.residentAssignment.assignAllHint', {
                occupied: occupiedUnits,
                total: totalUnits,
              })}
            </Text>
          </div>
        )}
        {!allUnitsHaveResidents && totalUnits === 0 && (
          <div style={{ marginTop: '12px' }}>
            <Text type="warning" style={{ fontSize: '14px' }}>
              {t('onboarding:cards.residentAssignment.noUnits', 'No apartment units were found. Add units to blocks first.')}
            </Text>
          </div>
        )}
      </div>
    </Card>
  );
};
